import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { assetUrl } from './design/DesignLayers';

// Gallery brand filter value that shows every partner's designs together (one partner = its id)
export const ALL_PARTNERS = 'partners';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-paper';

// Dark gold rather than the brand gold: white text on #B09536 is too faint to read at this size
// Shoppers see a partner as a collection ("The Hazmoonah Collection"), never as a "partner"
const CollectionLabel = ({ className = '' }) => (
  <span
    className={`shrink-0 inline-flex items-center rounded-full bg-[#6E5B1C] text-white font-bold uppercase tracking-[0.08em] ${className}`}
  >
    Collection
  </span>
);

export const collectionName = (name = '') =>
  /collection$/i.test(name.trim()) ? name.trim() : `The ${name.trim()} Collection`;

const partnerHref = (id) => `/templates?brand=${encodeURIComponent(id)}`;

const BrandLogo = ({ brand, className }) =>
  brand.logo_url ? (
    <img src={assetUrl(brand.logo_url)} alt={brand.name} className={className} loading="lazy" />
  ) : (
    <span className="text-[11px] sm:text-xs font-bold text-ink truncate">{brand.name}</span>
  );

/**
 * The tag on a partner's template card, like Amazon's "Sponsored" + brand store link: a clear
 * "Collection" label with their logo, and clicking it opens all of that partner's designs.
 * It sits over the card, not inside the card's link, because links can't be nested.
 */
export const PartnerTag = ({ brand, onOpen }) => (
  <Link
    to={partnerHref(brand.id)}
    onClick={onOpen}
    className={`absolute top-2 left-2 sm:top-3 sm:left-3 z-10 inline-flex items-center gap-1.5 h-7 sm:h-8 max-w-[calc(100%-1rem)] pl-1 pr-2 sm:pr-2.5 rounded-full bg-white/95 shadow-[0_2px_8px_rgba(26,26,26,0.18)] hover:shadow-[0_0_0_2px_#6E5B1C] transition-shadow ${focusRing}`}
    title={`See ${collectionName(brand.name)}`}
    aria-label={`${collectionName(brand.name)}. See all its designs`}
    data-testid="brand-tag"
  >
    <CollectionLabel className="h-5 sm:h-6 px-1.5 sm:px-2 text-[9px] sm:text-[10px]" />
    <BrandLogo brand={brand} className="h-4 sm:h-5 w-auto min-w-0 max-w-[6rem] object-contain" />
  </Link>
);

/** Header over the gallery when it shows one partner's designs (their "store") or all partners. */
export const PartnerBanner = ({ brand, brands, count, onOpen, onClear }) => {
  const single = Boolean(brand);
  return (
    <section
      className="gradient-border gb-cream rounded-2xl flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-4 sm:p-6 mb-8 sm:mb-10"
      aria-label={single ? collectionName(brand.name) : 'Our collections'}
      data-testid="partner-banner"
    >
      {single && (
        <div className="shrink-0 h-16 w-16 sm:h-20 sm:w-20 rounded-xl bg-white flex items-center justify-center p-2 shadow-[0_2px_8px_rgba(26,26,26,0.12)]">
          {brand.logo_url ? (
            <img src={assetUrl(brand.logo_url)} alt="" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="font-serif text-3xl font-semibold text-ink" aria-hidden="true">
              {(brand.name || '?').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      )}
      <div className="min-w-0 flex-1 flex flex-col gap-1.5">
        <CollectionLabel className="self-start h-6 px-2.5 text-[11px]" />
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink break-words">
          {single ? collectionName(brand.name) : 'Our collections'}
        </h2>
        <p className="text-sm sm:text-[15px] text-[#4A4640]">
          {count} design{count === 1 ? '' : 's'}
          {single ? ` by ${brand.name}, made for Azamein.` : ' from invitation studios we work with.'}
        </p>
        {!single && brands.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2" role="group" aria-label="Choose a collection">
            {brands.map((b) => (
              <Link
                key={b.id}
                to={partnerHref(b.id)}
                onClick={onOpen}
                className={`inline-flex items-center h-9 px-3 rounded-full bg-white shadow-[0_1px_4px_rgba(26,26,26,0.12)] hover:shadow-[0_0_0_2px_#6E5B1C] transition-shadow ${focusRing}`}
                data-testid={`partner-chip-${b.id}`}
              >
                <BrandLogo brand={b} className="h-5 w-auto max-w-[7rem] object-contain" />
              </Link>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClear}
        className={`shrink-0 self-start sm:self-center inline-flex items-center gap-2 h-11 px-5 rounded-full bg-ink text-paper text-sm font-semibold hover:bg-ink/90 transition-colors ${focusRing}`}
      >
        All designs
        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  );
};
